import { MdDragIndicator } from "react-icons/md";
import { useState, useEffect } from "react";
import React from "react";
import { MdDelete, MdModeEdit } from "react-icons/md";
import { HiDotsVertical } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import "../../assets/styles/category.css";
import { Reorder } from "framer-motion";
import { IoBagAdd } from "react-icons/io5";
import Toggle from "../../components/common/toggle";
import { useViewAdminUsers } from "../../hooks/adminuser/adminuser.showall";
import toast from "react-hot-toast";
import { useAdminUserUpdate } from "../../hooks/adminuser/adminuser.update";
import { useRemoveAdminUser } from "../../hooks/adminuser/adminuser.delete";
import AdminModal from "../../components/modal/admin.modal";
import Swal from "sweetalert2";

const AdminUserList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdminUserId, setEditingAdminUserId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "",
    password: "",
    profilePicture: null,
  });

  const { data: adminUsersResponse, isLoading, isError } = useViewAdminUsers();

  const [adminData, setAdminData] = useState([]);
  const [checkedMap, setCheckedMap] = useState({});
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);

  const adminUserUpdate = useAdminUserUpdate();
  const adminUserDelete = useRemoveAdminUser();

  useEffect(() => {
    if (adminUsersResponse?.data) {
      setAdminData(adminUsersResponse.data);

      const initialCheckedMap = {};
      adminUsersResponse.data.forEach((user) => {
        initialCheckedMap[user._id] = user.status === "active";
      });
      setCheckedMap(initialCheckedMap);

      adminUsersResponse.data.forEach((user) => {
        if (user.profilePicture) {
          const imageUrl = `${process.env.REACT_APP_BASE_IMAGE}/${user.profilePicture}`;
          fetch(imageUrl, {
            headers: {
              "ngrok-skip-browser-warning": "69420",
            },
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.blob();
            })
            .then((blob) => {
              const blobUrl = URL.createObjectURL(blob);
              setImageBlobUrls((prev) => ({
                ...prev,
                [user._id]: blobUrl,
              }));
            })
            .catch((err) => {
              console.error(`Failed to load image for user ${user._id}:`, err);
              setImageBlobUrls((prev) => ({
                ...prev,
                [user._id]: imageUrl,
              }));
            });
        }
      });
    }

    return () => {
      Object.values(imageBlobUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [adminUsersResponse]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOpenModal = () => {
    setEditingAdminUserId(null);
    setIsModalOpen(true);
    setFormData({
      name: "",
      email: "",
      role: "",
      password: "",
      profilePicture: null,
    });
  };

  const onToggleChange = async (id, newChecked) => {
    const newStatus = newChecked ? "active" : "inactive";

    setCheckedMap((prev) => ({
      ...prev,
      [id]: newChecked,
    }));

    try {
      await adminUserUpdate.mutateAsync({
        id,
        body: { status: newStatus },
      });
      toast.success(`User status updated to ${newStatus}`);
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update user status");
      // Revert the toggle on error
      setCheckedMap((prev) => ({
        ...prev,
        [id]: !newChecked,
      }));
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAdminUserId(null);
    setFormData({
      name: "",
      email: "",
      role: "",
      password: "",
      profilePicture: null,
    });
  };

  const handleEdit = (adminUser) => {
    setEditingAdminUserId(adminUser._id);
    setIsModalOpen(true);
  };

  const handleDelete = async (adminUser) => {
    if (!adminUser || !adminUser._id) {
      toast.error("Invalid admin user");
      return;  
    }

    // Close the dropdown menu immediately
    setOpenMenuId(null);

    const confirmDelete = await Swal.fire({
        title: "Delete Admin User?",
        text:"Are you sure you want to delete this admin user?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    });

    if (confirmDelete.isConfirmed) {
      try {
        await adminUserDelete.mutateAsync(adminUser._id);
        toast.success("Admin user deleted successfully");
      } catch (error) {
        console.error("Failed to delete admin user:", error);
        toast.error("Failed to delete admin user");
      }
    }
  };

  const handleMenuToggle = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const CategoryItem = ({ category }) => {
    const isMobile = window.innerWidth <= 768;

    React.useEffect(() => {
      if (!openMenuId) return;
      const handleClick = (e) => {
        if (!e.target.closest(".category-item")) setOpenMenuId(null);
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }, [openMenuId]);

    return (
      <div
        className="category-item"
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          listStyle: "none",
        }}
      >
        <span style={{ cursor: "grab", marginRight: 12 }}>
          <MdDragIndicator size={22} color="#898989" />
        </span>
        <span className="category-content" style={{ flex: 1 }}>
          {category.profilePicture && imageBlobUrls[category._id] && (
            <img
              src={imageBlobUrls[category._id]}
              alt={category.name}
              className="category-image"
              onError={(e) => {
                // Fallback to placeholder or hide on error
                e.target.style.display = "none";
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              marginRight: "10px",
            }}
          >
            <span className="category-names" style={{ display: "block" }}>
              {category.name || "Unnamed User"}
            </span>
            <span
              className="category-names"
              style={{
                marginTop: "5px",
                display: "block",
              }}
            >
              {category.email || "No email"}
            </span>
            <span
              className="category-names"
              style={{
                color: "green",
                fontSize: "clamp(0.75rem, 1.8vw, 0.9rem)",
                display: "block",
              }}
            >
              {category.role
                ? category.role.name
                : category.role?.permissions?.length
                  ? `${category.role.permissions.length} permission${
                      category.role.permissions.length !== 1 ? "s" : ""
                    }`
                  : "No permissions"}
            </span>
          </div>

          <Toggle
            onChange={(newChecked) => onToggleChange(category._id, newChecked)}
            checked={checkedMap[category._id] || false}
          />

          <div
            className="commonBtn"
            style={{ position: "relative", marginLeft: 8 }}
          >
            <span style={{ position: "relative" }}>
              <HiDotsVertical
                style={{ marginLeft: isMobile ? 6 : 10, cursor: "pointer" }}
                color="#898989"
                size={isMobile ? 18 : 20}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(
                    openMenuId === category._id ? null : category._id,
                  );
                }}
              />
              {openMenuId === category._id && (
                <div
                  className="category-dropdown-menu"
                  style={{ right: isMobile ? 0 : "0px", left: "auto" }}
                >
                  <div
                    className="category-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(category);
                    }}
                  >
                    <MdModeEdit color="#898989" /> Edit
                  </div>
                  <div
                    className="category-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(category);
                    }}
                  >
                    <MdDelete color="#898989" /> Delete
                  </div>
                </div>
              )}
            </span>
          </div>
        </span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Loading admin users...
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
        Error loading admin users
      </div>
    );
  }

  return (
    <>
      <button
        className="commonButton"
        onClick={handleOpenModal}
        style={{ marginLeft: "auto" }}
      >
        <IoBagAdd size={20} color="#ffffff" style={{ marginRight: 8 }} /> Add
        New Admin
      </button>

      <AdminModal
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        editingAdminUserId={editingAdminUserId}
      />

      <div
        className="categories-container"
        style={{ listStyleType: "none", paddingLeft: 0 }}
      >
        {adminData.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
            No admin users found. Click "Add New Admin" to create one.
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={adminData}
            onReorder={setAdminData}
            style={{ listStyleType: "none", paddingLeft: 0 }}
          >
            {adminData.map((cat) => (
              <Reorder.Item
                key={cat._id}
                value={cat}
                style={{ listStyleType: "none" }}
              >
                <CategoryItem category={cat} />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}
      </div>
    </>
  );
};

export default AdminUserList;
